import { z } from 'zod';

import { assertOk, parseJson } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

const requestJson = async <T>(
  path: string,
  schema: z.ZodType<T>,
  {
    method = 'GET',
    signal,
    headers,
  }: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    signal?: AbortSignal;
    headers?: Record<string, string>;
  } = {},
): Promise<T> => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(path, {
    method,
    signal,
    headers,
  });

  await assertOk(response);
  return parseJson(response, schema);
};

const ChannelMetricsSchema = z
  .object({
    sent: z.number(),
    delivered: z.number(),
    failed: z.number(),
    bounced: z.number(),
  })
  .passthrough();

const NotificationSummarySchema = z
  .object({
    windowHours: z.number(),
    totals: ChannelMetricsSchema.extend({
      overallSent: z.number(),
      deliverabilityRate: z.number(),
      bounceRate: z.number(),
    }),
    byChannel: z.object({
      email: ChannelMetricsSchema,
      sms: ChannelMetricsSchema,
    }),
  })
  .passthrough();

const NotificationSummaryResponseSchema = z
  .object({
    ok: z.literal(true),
    metrics: NotificationSummarySchema,
  })
  .passthrough();

const NotificationTimelinePointSchema = z
  .object({
    day: z.string(),
    channel: z.enum(['email', 'sms']),
    sent: z.number(),
    delivered: z.number(),
    failed: z.number(),
    bounced: z.number(),
  })
  .passthrough();

const NotificationTimelineResponseSchema = z
  .object({
    ok: z.literal(true),
    points: z.array(NotificationTimelinePointSchema),
  })
  .passthrough();

export type ChannelMetrics = z.infer<typeof ChannelMetricsSchema>;
export type NotificationSummary = z.infer<typeof NotificationSummarySchema>;
export type NotificationTimelinePoint = z.infer<typeof NotificationTimelinePointSchema>;

const SendNotificationResponseSchema = z
  .object({
    ok: z.literal(true),
    message: z.string(),
  })
  .passthrough();

export const fetchNotificationSummary = async (
  tenantSlug: string,
  options: { signal?: AbortSignal } = {},
): Promise<NotificationSummary> => {
  const result = await requestJson(
    '/api/notifications/metrics/summary',
    NotificationSummaryResponseSchema,
    {
      signal: options.signal,
      headers: {
        Accept: 'application/json',
        'X-Tenant': tenantSlug,
      },
    },
  );

  return result.metrics;
};

export const fetchNotificationTimeline = async (
  tenantSlug: string,
  options: { signal?: AbortSignal; days?: number } = {},
): Promise<NotificationTimelinePoint[]> => {
  const query = new URLSearchParams();
  if (options.days) {
    query.set('days', String(options.days));
  }

  const path =
    query.size > 0 ? `/api/notifications/metrics/timeline?${query.toString()}` : '/api/notifications/metrics/timeline';

  const result = await requestJson(
    path,
    NotificationTimelineResponseSchema,
    {
      signal: options.signal,
      headers: {
        Accept: 'application/json',
        'X-Tenant': tenantSlug,
      },
    },
  );

  return result.points;
};

export const sendNotification = async (payload: {
  channel: 'email' | 'sms';
  templateId: string;
  to: string;
  data?: Record<string, unknown>;
  profileId?: string;
  metadata?: Record<string, unknown>;
}) => {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch('/api/notifications/send', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      channel: payload.channel,
      templateId: payload.templateId,
      to: payload.to,
      data: payload.data ?? {},
      profileId: payload.profileId,
      metadata: payload.metadata,
    }),
  });

  await assertOk(response);
  return parseJson(response, SendNotificationResponseSchema);
};
